import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { AddButton } from '@trinity/chat-components/components/presentation/DigitalTwinsAddButton';
import { FormButtons } from '@trinity/chat-components/components/presentation/FormButtons';
import { MAX_REPEAT_COUNT } from '@trinity/management-app-client/app-v1/form-schemas';
import { RepeatableFormBlock } from './DynamicForm.repeatable.block';
import type {
  FormFieldValue,
  RepeatableAIContext,
  RepeatableBlock,
  RepeatableTemplate,
} from './DynamicForm.repeatable.utils';
import { createEmptyBlock } from './DynamicForm.repeatable.utils';

const TopBar = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 12px 16px 0;
`;

const StyledAddButton = styled(AddButton)`
  margin-top: 14px;
`;

export type RepeatableFormModalProps = {
  open: boolean;
  template: RepeatableTemplate;
  initialBlocks: RepeatableBlock[];
  fieldErrors: Record<string, string>;
  onSave: (blocks: RepeatableBlock[]) => void;
  onCancel: () => void;
  aiContext: RepeatableAIContext;
};

export function RepeatableFormModal({
  open,
  template,
  initialBlocks,
  fieldErrors,
  onSave,
  onCancel,
  aiContext,
}: RepeatableFormModalProps): React.JSX.Element {
  const [draft, setDraft] = useState<RepeatableBlock[]>(initialBlocks);

  useEffect(() => {
    if (open) {
      setDraft(initialBlocks);
    }
    // Re-seed the draft every time the dialog transitions closed -> open, so X always
    // discards cleanly and a stale draft can never leak into the next edit session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleBlockChange = (blockIndex: number, fieldName: string, value: FormFieldValue): void => {
    setDraft((prev) => {
      const current = prev[blockIndex];
      if (!current) {
        return prev;
      }
      const next = [...prev];
      next[blockIndex] = { ...current, [fieldName]: value };
      return next;
    });
  };

  const handleAdd = (): void => {
    setDraft((prev) => (prev.length >= MAX_REPEAT_COUNT ? prev : [...prev, createEmptyBlock(template)]));
  };

  const handleDelete = (blockIndex: number): void => {
    setDraft((prev) => prev.filter((_, i) => i !== blockIndex));
  };

  return (
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="md">
      <TopBar>
        <FormButtons onSubmit={() => onSave(draft)} onCancel={onCancel} size={20} />
      </TopBar>
      <DialogContent>
        {draft.map((block, index) => (
          <RepeatableFormBlock
            key={index}
            template={template}
            index={index}
            block={block}
            fieldErrors={fieldErrors}
            onChange={(fieldName, value) => handleBlockChange(index, fieldName, value)}
            onDelete={index > 0 ? () => handleDelete(index) : undefined}
            aiContext={aiContext}
          />
        ))}
        {draft.length < MAX_REPEAT_COUNT && <StyledAddButton onClick={handleAdd}>Add</StyledAddButton>}
      </DialogContent>
    </Dialog>
  );
}
